/**
 * "{agent} is ready to test" — the App shell's banner (`design/WeTalk.dc.html` 2935–2947, the export of 2026-10-10,
 * `readyBanner`). It replaces the export's bottom-right toast: once a build this tab started has finished, every screen
 * but Building and the agent's own page opens with it — "Built from menu-autumn.pdf. Speaks Greek." and "Connect a
 * number when you want it to take calls" while nothing reaches the agent — with Open agent and Dismiss.
 *
 * WHAT IT READS. The wizard, on Build, records the build job, the VoiceAgent and what the person gave it — the names of
 * the documents attached and the languages chosen ({@link rememberReadyBuild}) — in the tab's session storage, so a
 * reload keeps it and a closed tab forgets it. The banner reads the build itself (`getBuildJob`, the Building screen's
 * read and cache key) every few seconds until it is finished; the agent's name and whether a number reaches it come
 * from that read, never from the record. A build that failed drops the record: Building and the notification centre
 * say why, and a "ready" banner would not be true.
 *
 * Gone on Dismiss, on Open agent, and once the person has reached the agent's own page (the export's `go.agent` and
 * `dismissToast` both clear it).
 */
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Button, Icon } from "@wetalk/ui";
import { useEffect, useState, type ReactNode } from "react";

import { GLYPH } from "./glyph.js";
import { useGateway } from "../api/provider.js";
import { languageNameOf } from "../api/conversation-view.js";
import { portalText, usePortalTranslation } from "../i18n/portal.js";

const STORE_KEY = "wetalk.ready-build.v1";

/** How often the build is read again while it is not finished (the Building screen reads every 4 s). */
const REREAD_MS = 5_000;

/** What the wizard knows when it starts a build, kept for the banner. */
export interface ReadyBuildRecord {
  readonly buildJobId: string;
  readonly voiceAgentId: string;
  /** The names of the documents attached, in the template's order; empty when none were. */
  readonly documentName: readonly string[];
  /** The languages chosen, the greeting language first, in words. */
  readonly languageName: readonly string[];
  /** Stable language codes let a saved build use the current interface language. */
  readonly languageCode?: readonly string[];
}

function tabStore(): Storage | null {
  try {
    // glossary-lint-disable-next-line session -- the DOM's own sessionStorage API, not a WeTalk session
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function isTextList(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((one) => typeof one === "string");
}

/** The record this tab keeps, or `null` — none, unreadable, or not one this module wrote. */
export function recallReadyBuild(): ReadyBuildRecord | null {
  try {
    const raw = tabStore()?.getItem(STORE_KEY);
    if (raw === null || raw === undefined) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    const { buildJobId, voiceAgentId, documentName, languageName } = record;
    if (typeof buildJobId !== "string" || typeof voiceAgentId !== "string") return null;
    if (!isTextList(documentName) || !isTextList(languageName)) return null;
    if (record["languageCode"] !== undefined && !isTextList(record["languageCode"])) return null;
    return { buildJobId, voiceAgentId, documentName, languageName, ...(isTextList(record["languageCode"]) ? { languageCode: record["languageCode"] } : {}) };
  } catch {
    return null;
  }
}

/** Kept by the wizard when a build starts; a later build replaces an earlier one. */
export function rememberReadyBuild(record: ReadyBuildRecord): void {
  try {
    tabStore()?.setItem(STORE_KEY, JSON.stringify(record));
  } catch {
    // A private window or blocked storage: the banner is a convenience, the Agents list still shows the agent.
  }
}

export function forgetReadyBuild(): void {
  try {
    tabStore()?.removeItem(STORE_KEY);
  } catch {
    // As above.
  }
}

/** "a.pdf", "a.pdf and b.csv", "a, b and c". */
function inWords(word: readonly string[]): string {
  if (word.length <= 1) return word.join("");
  return portalText("core", "merge.ready_banner.list_and", { first: word.slice(0, -1).join(", "), last: String(word.at(-1)) });
}

/** The banner's second line, from what the person gave the build and whether a number reaches the agent. */
export function readyBannerDekOf(record: ReadyBuildRecord, reachable: boolean): string {
  const names = record.languageCode === undefined ? record.languageName : record.languageCode.map(languageNameOf);
  return [
    record.documentName.length === 0 ? null : portalText("core", "merge.ready_banner.built_from", { documents: inWords(record.documentName) }),
    names.length === 0 ? null : portalText("core", "merge.ready_banner.speaks", { languages: names.join(", ") }),
    reachable ? null : portalText("core", "merge.ready_banner.connect_number"),
  ]
    .filter((one): one is string => one !== null)
    .join(" ");
}

/** Building, and the agent's own page (`/agents/<id>/…`, not `/agents/new`), draw no banner. */
export function readyBannerHiddenOn(pathname: string, voiceAgentId: string): "building" | "agent" | null {
  if (pathname.startsWith("/building/")) return "building";
  if (pathname === `/agents/${voiceAgentId}` || pathname.startsWith(`/agents/${voiceAgentId}/`)) return "agent";
  return null;
}

export function ReadyBanner(): ReactNode {
  const { t } = usePortalTranslation("core");
  const gateway = useGateway();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // Read on every render — a session storage read, cheap: the page changing (the wizard writes the record and then
  // navigates to Building) re-renders, and Dismiss or Open agent re-render through `setForgotten`.
  const [, setForgotten] = useState(0);
  const record = recallReadyBuild();

  const build = useQuery({
    queryKey: ["build-job", record?.buildJobId ?? "none"],
    queryFn: () => gateway.getBuildJob(record?.buildJobId ?? ""),
    enabled: record !== null,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === "succeeded" || state === "failed" ? false : REREAD_MS;
    },
    retry: false,
  });

  const state = build.data?.state;
  const hidden = record === null ? null : readyBannerHiddenOn(pathname, record.voiceAgentId);
  const forget = (): void => {
    forgetReadyBuild();
    setForgotten((count) => count + 1);
  };
  // A failed build, or the agent's page reached: the record goes from storage; the banner is not drawn either way,
  // and the next page reads the record afresh.
  useEffect(() => {
    if (record !== null && (state === "failed" || (state === "succeeded" && hidden === "agent"))) forgetReadyBuild();
  }, [record, state, hidden]);

  if (record === null || build.data === undefined || state !== "succeeded" || hidden !== null) return null;
  const voiceAgentId = record.voiceAgentId;
  return (
    <div className="c-alert c-alert-ready" role="status">
      <Icon glyph={GLYPH.checkCircle} size="xl" tone="accent" />
      <span className="c-alert-body">
        <span className="c-alert-title">{t("merge.ready_banner.title", { name: build.data.voiceAgentName })}</span>
        <span className="c-alert-dek">{readyBannerDekOf(record, build.data.reachable)}</span>
      </span>
      <span className="c-alert-actions">
        <Button
          variant="primary"
          onClick={() => {
            forget();
            void navigate({ to: "/agents/$voiceAgentId", params: { voiceAgentId } });
          }}
        >
          {t("merge.ready_banner.open_agent")}
        </Button>
        <Button variant="ghost" onClick={forget}>
          {t("merge.ready_banner.dismiss")}
        </Button>
      </span>
    </div>
  );
}
